import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export type SystemAdminScopeMode = 'company' | 'global';

@Injectable({
  providedIn: 'root',
})
export class SystemAdminContextService {
  private readonly companyIdStorageKey = 'clinic_system_admin_company_id';
  private readonly clinicIdStorageKey = 'clinic_system_admin_clinic_id';
  private readonly scopeModeStorageKey = 'clinic_system_admin_scope_mode';
  private readonly persistenceEnabledStorageKey = 'clinic_system_admin_scope_persistence_enabled';

  private readonly persistenceEnabledSubject = new BehaviorSubject<boolean>(this.readPersistenceEnabled());

  private readonly companyIdSubject = new BehaviorSubject<string | null>(
    this.persistenceEnabledSubject.value ? this.readStored(this.companyIdStorageKey) : null,
  );
  private readonly clinicIdSubject = new BehaviorSubject<string | null>(
    this.persistenceEnabledSubject.value ? this.readStored(this.clinicIdStorageKey) : null,
  );
  private readonly scopeModeSubject = new BehaviorSubject<SystemAdminScopeMode>(
    this.persistenceEnabledSubject.value ? this.readScopeMode() : 'company',
  );
  private readonly refreshSubject = new Subject<void>();

  public readonly persistenceEnabled$ = this.persistenceEnabledSubject.asObservable();
  public readonly companyId$ = this.companyIdSubject.asObservable();
  public readonly clinicId$ = this.clinicIdSubject.asObservable();
  public readonly scopeMode$ = this.scopeModeSubject.asObservable();
  public readonly refresh$ = this.refreshSubject.asObservable();

  public isPersistenceEnabled(): boolean {
    return this.persistenceEnabledSubject.value;
  }

  public setPersistenceEnabled(enabled: boolean): void {
    if (enabled === this.persistenceEnabledSubject.value) {
      return;
    }

    this.persistenceEnabledSubject.next(enabled);

    if (enabled) {
      this.safeSetItem(this.persistenceEnabledStorageKey, '1');
      this.persistCurrentState();
      return;
    }

    this.safeRemoveItem(this.persistenceEnabledStorageKey);
    this.clearPersistedScope();
  }

  public getCompanyId(): string | null {
    return this.companyIdSubject.value;
  }

  public getClinicId(): string | null {
    return this.clinicIdSubject.value;
  }

  public getScopeMode(): SystemAdminScopeMode {
    return this.scopeModeSubject.value;
  }

  public isGlobalScope(): boolean {
    return this.scopeModeSubject.value === 'global';
  }

  public setScopeMode(mode: SystemAdminScopeMode): void {
    if (mode === this.scopeModeSubject.value) {
      this.persistScopeMode(mode);
      return;
    }

    this.scopeModeSubject.next(mode);
    this.persistScopeMode(mode);
    this.refreshSubject.next();
  }

  public setCompanyId(companyId: string | null): void {
    const normalized = this.normalize(companyId);
    if (normalized === this.companyIdSubject.value) {
      this.persistCompanyId(normalized);
      return;
    }

    this.companyIdSubject.next(normalized);

    if (normalized) {
      this.persistCompanyId(normalized);
      this.refreshSubject.next();
      return;
    }

    this.persistCompanyId(null);
    this.setClinicId(null);
  }

  public setClinicId(clinicId: string | null): void {
    const normalized = this.normalize(clinicId);
    if (normalized === this.clinicIdSubject.value) {
      this.persistClinicId(normalized);
      return;
    }

    this.clinicIdSubject.next(normalized);

    if (normalized) {
      this.persistClinicId(normalized);
      this.refreshSubject.next();
      return;
    }

    this.persistClinicId(null);
    this.refreshSubject.next();
  }

  public clear(): void {
    this.setCompanyId(null);
    this.setClinicId(null);
    this.setScopeMode('company');
  }

  public triggerRefresh(): void {
    this.refreshSubject.next();
  }

  private readStored(key: string): string | null {
    try {
      return this.normalize(localStorage.getItem(key));
    } catch {
      return null;
    }
  }

  private normalize(value: string | null | undefined): string | null {
    const normalized = value?.trim() ?? '';
    return normalized || null;
  }

  private readScopeMode(): SystemAdminScopeMode {
    try {
      const value = localStorage.getItem(this.scopeModeStorageKey);
      return value === 'global' ? 'global' : 'company';
    } catch {
      return 'company';
    }
  }

  private readPersistenceEnabled(): boolean {
    try {
      return localStorage.getItem(this.persistenceEnabledStorageKey) === '1';
    } catch {
      return false;
    }
  }

  private persistCurrentState(): void {
    this.persistCompanyId(this.companyIdSubject.value);
    this.persistClinicId(this.clinicIdSubject.value);
    this.persistScopeMode(this.scopeModeSubject.value);
  }

  private clearPersistedScope(): void {
    this.safeRemoveItem(this.companyIdStorageKey);
    this.safeRemoveItem(this.clinicIdStorageKey);
    this.safeRemoveItem(this.scopeModeStorageKey);
  }

  private persistCompanyId(value: string | null): void {
    if (!this.persistenceEnabledSubject.value) {
      this.safeRemoveItem(this.companyIdStorageKey);
      return;
    }

    if (value) {
      this.safeSetItem(this.companyIdStorageKey, value);
      return;
    }

    this.safeRemoveItem(this.companyIdStorageKey);
  }

  private persistClinicId(value: string | null): void {
    if (!this.persistenceEnabledSubject.value) {
      this.safeRemoveItem(this.clinicIdStorageKey);
      return;
    }

    if (value) {
      this.safeSetItem(this.clinicIdStorageKey, value);
      return;
    }

    this.safeRemoveItem(this.clinicIdStorageKey);
  }

  private persistScopeMode(value: SystemAdminScopeMode): void {
    if (!this.persistenceEnabledSubject.value) {
      this.safeRemoveItem(this.scopeModeStorageKey);
      return;
    }

    this.safeSetItem(this.scopeModeStorageKey, value);
  }

  private safeSetItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore storage access errors
    }
  }

  private safeRemoveItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore storage access errors
    }
  }
}
